import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Stack, Button } from 'react-bootstrap';
import { AppLoader } from '../../../Components/common/AppLoader/AppLoader';
import { ArticleCard } from '../../../Components/ui/ArticleCard/ArticleCard';
import { fetchSpecialties } from '../../../api/doctorApi';
import {
  fetchNewsArticles,
  mapArticleToHomeCardProps,
} from '../../../api/newsArticlesApi';
import { getSpecialtyNames } from '../../../utils/doctorHelpers';
import './newsarticles.css';

export const Newsarticles = ({ gridMode = false }) => {
  const [articles, setArticles] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [articleRows, specialtyRows] = await Promise.all([
          fetchNewsArticles(),
          fetchSpecialties(),
        ]);
        if (cancelled) return;

        const specialtyNames = getSpecialtyNames(specialtyRows);
        const mapped = articleRows.map((article) =>
          mapArticleToHomeCardProps(article, specialtyNames),
        );

        setArticles(mapped);
        setSpecialties(specialtyNames);
        setActiveCategory(
          specialtyNames[0]
            || mapped.find((article) => article.medicalSpecialty)?.medicalSpecialty
            || '',
        );
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load news articles.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (gridMode) return articles.slice(0, 3);
    if (!activeCategory) return articles;
    return articles.filter((article) => article.medicalSpecialty === activeCategory);
  }, [articles, activeCategory, gridMode]);

  return (
    <section className="news">
      <Container fluid className="news__container">
        <Stack direction="horizontal" className="news__header">
          <Stack gap={1}>
            <h2 className="news__title">News And Articles</h2>
            <p className="news__subtitle">
              Learn from leading doctors and specialists through focused,
              digestible video content.
            </p>
          </Stack>
          <Button className="partner-hospitals__view-all" onClick={() => navigate('/news-articles')}>
            View All &nbsp;→
          </Button>
        </Stack>

        {loading && (
          <AppLoader className="py-5" />
        )}

        {!loading && error && (
          <p className="text-center py-4 text-danger">{error}</p>
        )}

        {!loading && !error && (
          <>
            {!gridMode && specialties.length > 0 && (
              <div className="news__tabs-wrap">
                <Stack direction="horizontal" gap={2} className="news__tabs">
                  {specialties.map((specialty) => (
                    <Button
                      key={specialty}
                      className={`news__tab ${activeCategory === specialty ? 'news__tab--active' : ''}`}
                      onClick={() => setActiveCategory(specialty)}
                    >
                      {specialty}
                    </Button>
                  ))}
                </Stack>
              </div>
            )}

            {gridMode ? (
              <div className="news__cards-grid">
                {filtered.length > 0 ? (
                  filtered.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))
                ) : (
                  <p className="news__empty">No articles available yet.</p>
                )}
              </div>
            ) : (
              <div className="news__cards-wrap">
                <Stack direction="horizontal" gap={3} className="news__cards">
                  {filtered.length > 0 ? (
                    filtered.map((article) => (
                      <ArticleCard key={article.id} {...article} />
                    ))
                  ) : (
                    <p className="news__empty">No articles found for this specialty.</p>
                  )}
                </Stack>
              </div>
            )}
          </>
        )}
      </Container>
    </section>
  );
};
